import { createTheme } from "@mui/material";

import { semanticShadows } from "@dfp/isomorphic-design-tokens";

import {
  accordionDetailsTheme,
  accordionSummaryTheme,
  accordionTheme,
  alertTheme,
  alertTitleTheme,
  autocompleteTheme,
  avatarTheme,
  badgeTheme,
  breadcrumbsTheme,
  buttonGroupTheme,
  buttonTheme,
  cardActionsTheme,
  cardContentTheme,
  cardHeaderTheme,
  cardTheme,
  checkboxTheme,
  chipTheme,
  cssBaselineTheme,
  dialogActionsTheme,
  dialogContentTheme,
  dialogTheme,
  dialogTitleTheme,
  dividerTheme,
  filledInputTheme,
  formControlLabelTheme,
  formHelperTextTheme,
  formLabelTheme,
  iconButtonTheme,
  inputBaseTheme,
  linkTheme,
  listItemButtonTheme,
  listItemIconTheme,
  loadingButtonTheme,
  menuItemTheme,
  menuTheme,
  outlinedInputTheme,
  paperTheme,
  popoverTheme,
  selectTheme,
  skeletonTheme,
  tableCellTheme,
  tableRowTheme,
  tabsTheme,
  tabTheme,
  toggleButtonGroupTheme,
  toggleButtonTheme,
  tooltipTheme,
  typographyTheme,
} from "./components/index.ts";
import { palette } from "./palette.ts";
import { typography } from "./typography.ts";

export type { StyleOverrideProps } from "./components/button.theme.ts";

export const standardTheme = createTheme({
  cssVariables: true,
  components: {
    MuiCssBaseline: cssBaselineTheme,
    MuiLoadingButton: loadingButtonTheme,
    MuiButton: buttonTheme,
    MuiIconButton: iconButtonTheme,
    MuiButtonGroup: buttonGroupTheme,
    MuiCheckbox: checkboxTheme,
    MuiToggleButtonGroup: toggleButtonGroupTheme,
    MuiToggleButton: toggleButtonTheme,
    MuiLink: linkTheme,
    MuiBreadcrumbs: breadcrumbsTheme,
    MuiPopover: popoverTheme,
    MuiSkeleton: skeletonTheme,
    MuiTableCell: tableCellTheme,
    MuiDivider: dividerTheme,
    MuiPaper: paperTheme,
    MuiBadge: badgeTheme,
    MuiInputBase: inputBaseTheme,
    MuiOutlinedInput: outlinedInputTheme,
    MuiAutocomplete: autocompleteTheme,
    MuiSelect: selectTheme,
    MuiFilledInput: filledInputTheme,
    MuiAccordion: accordionTheme,
    MuiAccordionSummary: accordionSummaryTheme,
    MuiAccordionDetails: accordionDetailsTheme,
    MuiAlert: alertTheme,
    MuiAlertTitle: alertTitleTheme,
    MuiChip: chipTheme,
    MuiAvatar: avatarTheme,
    MuiCard: cardTheme,
    MuiCardHeader: cardHeaderTheme,
    MuiCardContent: cardContentTheme,
    MuiCardActions: cardActionsTheme,
    MuiTooltip: tooltipTheme,
    MuiFormHelperText: formHelperTextTheme,
    MuiFormControlLabel: formControlLabelTheme,
    MuiFormLabel: formLabelTheme,
    MuiTabs: tabsTheme,
    MuiTab: tabTheme,
    MuiMenu: menuTheme,
    MuiMenuItem: menuItemTheme,
    MuiListItemButton: listItemButtonTheme,
    MuiListItemIcon: listItemIconTheme,
    MuiTableRow: tableRowTheme,
    MuiDialog: dialogTheme,
    MuiDialogTitle: dialogTitleTheme,
    MuiDialogActions: dialogActionsTheme,
    MuiDialogContent: dialogContentTheme,
    MuiTypography: typographyTheme,
  },
  avatarSize: {
    64: {
      width: "64px",
      height: "64px",
      borderRadius: "24px",
    },
    56: {
      width: "56px",
      height: "56px",
      borderRadius: "24px",
    },
    48: {
      width: "48px",
      height: "48px",
      borderRadius: "20px",
    },
    40: {
      width: "40px",
      height: "40px",
      borderRadius: "16px",
    },
    32: {
      width: "32px",
      height: "32px",
      borderRadius: "12px",
    },
    24: {
      width: "24px",
      height: "24px",
      borderRadius: "8px",
    },
    20: {
      width: "20px",
      height: "20px",
      borderRadius: "8px",
    },
    16: {
      width: "16px",
      height: "16px",
      borderRadius: "6px",
    },
  },
  borderRadius: {
    xxs: "4px",
    xs: "8px",
    sm: "12px",
    md: "16px",
    lg: "20px",
  },
  opacity: {
    disabled: 0.45,
  },
  customShadows: {
    card: semanticShadows.card,
    cardHover: semanticShadows.cardHover,
    modal: semanticShadows.modal,
    rightPanel: semanticShadows.rightPanel,
    dropdown: semanticShadows.dropdown,
    tab: semanticShadows.tab,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 760,
      md: 1024,
      lg: 1260,
      xl: 1920,
    },
  },
  palette,
  typography,
});
